import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DashboardLayout from "../components/DashboardLayout";
import {
  CreateNewEvent,
  CreateNewsEntry,
  CreateRewardCatalogEntry,
  DeleteEvent,
  DeleteNewsEntry,
  DeleteRewardCatalogEntry,
  DeleteUser,
  EventoTask,
  GetAllEvents,
  GetAllNews,
  GetAllUsers,
  GetRewardsCatalog,
  isNewsFinished,
  NewsTask,
  RewardCatalogEntry,
  RewardCatalogEntryInput,
  Task,
  UpdateEvent,
  UpdateNewsEntry,
  UpdateRewardCatalogEntry,
  UpdateUserAdmin,
} from "../firebase-config";
import { invalidateRewardsCatalog } from "../hooks/useRewardsCatalog";
import XpImportTab from "../components/XpImportTab";

import YouTubeEmbed from "../components/YouTubeEmbed";
import { getYouTubeId } from "../utils/youtube";

import { parseXpReward } from "../components/XpRewardSection";

import InscriptosModal, { InscriptosTarget } from "../components/InscriptosModal";
import { DeleteAllInscriptos } from "../inscripciones";

import { SyncAllPublicProfiles } from "../firebase-chat";

const { TextArea } = Input;

// ---------- Tipos auxiliares usados solo mientras se edita ----------
interface EditableSkill {
  Materia: string;
  Progress: number;
}

interface EditableLogro {
  Name: string;
  Description: string;
  Status: string;
  Icon: string;
}

interface EditableNotification {
  Title: string;
  Description: string;
  Checked: boolean;
  Icon: string;
}

const emptyLogro = (): EditableLogro => ({
  Name: "",
  Description: "",
  Status: "Completado",
  Icon: "",
});

const emptyNotification = (): EditableNotification => ({
  Title: "",
  Description: "",
  Checked: false,
  Icon: "",
});

const REWARD_CATEGORY_OPTIONS = [
  { value: "special", label: "special" },
  { value: "avatar", label: "avatar" },
  { value: "badge", label: "badge" },
  { value: "theme", label: "theme" },
];

const REWARD_RARITY_OPTIONS = [
  { value: "common", label: "common" },
  { value: "rare", label: "rare" },
  { value: "epic", label: "epic" },
  { value: "legendary", label: "legendary" },
];

const REWARD_TONE_OPTIONS = [
  { value: "teal", label: "teal" },
  { value: "sunset", label: "sunset" },
  { value: "sky", label: "sky" },
  { value: "placeholder", label: "placeholder" },
];

// Etiqueta de la columna "Inscripción" en las tablas de noticias y eventos.
const renderInscripcionTag = (abierta: unknown) =>
  abierta === true ? <Tag color="green">Abierta</Tag> : "-";

const Admin: React.FC = () => {
  // ══════════════════════════════════════════════════════════
  // USUARIOS
  // ══════════════════════════════════════════════════════════
  const [users, setUsers] = useState<Task[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [editingUser, setEditingUser] = useState<Task | null>(null);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [userForm] = Form.useForm();

  const [skillsDraft, setSkillsDraft] = useState<EditableSkill[]>([]);
  const [logrosDraft, setLogrosDraft] = useState<EditableLogro[]>([]);
  const [notificationsDraft, setNotificationsDraft] = useState<
    EditableNotification[]
  >([]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const allUsers = await GetAllUsers();
      setUsers(allUsers);
    } catch (error) {
      message.error("No se pudieron cargar los usuarios.");
    } finally {
      setLoadingUsers(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    if (!query) return users;
    return users.filter(
      (u) =>
        u.Nombre?.toLowerCase().includes(query) ||
        u.Email?.toLowerCase().includes(query),
    );
  }, [users, userSearch]);

  const openEditUser = (targetUser: Task) => {
    setEditingUser(targetUser);
    userForm.setFieldsValue({
      Nombre: targetUser.Nombre,
      Email: targetUser.Email,
      Rol: targetUser.Rol || "regular",
      Nivel: targetUser.Nivel || 1,
      CurrentExp: targetUser.CurrentExp || 0,
      Puntos: targetUser.Puntos || 0,
      PortraitImg: targetUser.PortraitImg || "",
    });
    setSkillsDraft(
      (targetUser.Skill || []).map((s) => ({
        Materia: s.Materia,
        Progress: s.Progress,
      })),
    );
    setLogrosDraft(
      (targetUser.Logros || []).map((l) => ({
        Name: l.Name,
        Description: l.Description,
        Status: l.Status,
        Icon: typeof l.Icon === "string" ? l.Icon : "",
      })),
    );
    setNotificationsDraft(
      (targetUser.Notifications || []).map((n) => ({
        Title: n.Title,
        Description: n.Description,
        Checked: n.Checked,
        Icon: typeof n.Icon === "string" ? n.Icon : "",
      })),
    );
    setUserDrawerOpen(true);
  };

  const closeUserDrawer = () => {
    setUserDrawerOpen(false);
    setEditingUser(null);
    userForm.resetFields();
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    try {
      const values = await userForm.validateFields();
      setSavingUser(true);
      await UpdateUserAdmin(editingUser.id, {
        Nombre: values.Nombre,
        Rol: values.Rol,
        Nivel: values.Nivel,
        CurrentExp: values.CurrentExp,
        Puntos: values.Puntos,
        PortraitImg: values.PortraitImg,
        Skill: skillsDraft,
        Logros: logrosDraft,
        Notifications: notificationsDraft,
      });
      message.success("Usuario actualizado correctamente.");
      closeUserDrawer();
      void loadUsers();
    } catch (error: any) {
      if (error?.errorFields) return; // error de validación del form, antd ya lo muestra
      message.error("No se pudo guardar el usuario.");
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (targetUser: Task) => {
    try {
      await DeleteUser(targetUser.id);
      message.success(
        `Documento de ${targetUser.Email || targetUser.id} eliminado.`,
      );
      void loadUsers();
    } catch (error) {
      message.error("No se pudo eliminar el usuario.");
    }
  };

  const userColumns = [
    { title: "Nombre", dataIndex: "Nombre", key: "Nombre" },
    { title: "Email", dataIndex: "Email", key: "Email" },
    {
      title: "Rol",
      dataIndex: "Rol",
      key: "Rol",
      render: (rol: string) => (
        <Tag color={rol === "admin" ? "gold" : "blue"}>{rol || "regular"}</Tag>
      ),
    },
    { title: "Nivel", dataIndex: "Nivel", key: "Nivel", width: 80 },
    { title: "Puntos", dataIndex: "Puntos", key: "Puntos", width: 90 },
    {
      title: "Acciones",
      key: "actions",
      width: 170,
      render: (_: unknown, record: Task) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditUser(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar este usuario de la base de datos?"
            description="Borra su documento en Firestore. No borra su cuenta de acceso (eso se sigue manejando desde Unity)."
            okText="Eliminar"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={() => handleDeleteUser(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ---------- Helpers de edición: Skills ----------
  const updateSkillDraft = (
    index: number,
    field: keyof EditableSkill,
    value: string | number,
  ) => {
    setSkillsDraft((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value } as EditableSkill;
      return next;
    });
  };
  const addSkillDraft = () =>
    setSkillsDraft((prev) => [...prev, { Materia: "Nueva materia", Progress: 0 }]);
  const removeSkillDraft = (index: number) =>
    setSkillsDraft((prev) => prev.filter((_, i) => i !== index));

  // ---------- Helpers de edición: Logros ----------
  const updateLogroDraft = (
    index: number,
    field: keyof EditableLogro,
    value: string,
  ) => {
    setLogrosDraft((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const addLogroDraft = () => setLogrosDraft((prev) => [...prev, emptyLogro()]);
  const removeLogroDraft = (index: number) =>
    setLogrosDraft((prev) => prev.filter((_, i) => i !== index));

  // ---------- Helpers de edición: Notificaciones ----------
  const updateNotificationDraft = (
    index: number,
    field: keyof EditableNotification,
    value: string | boolean,
  ) => {
    setNotificationsDraft((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value } as EditableNotification;
      return next;
    });
  };
  const addNotificationDraft = () =>
    setNotificationsDraft((prev) => [...prev, emptyNotification()]);
  const removeNotificationDraft = (index: number) =>
    setNotificationsDraft((prev) => prev.filter((_, i) => i !== index));

  // ══════════════════════════════════════════════════════════
  // INSCRIPTOS (agregado 11/10/2026)
  // Evento o noticia cuya lista de inscriptos está abierta (null = cerrada).
  // Se abre tocando la fila en la tabla o el botón "Inscriptos".
  // ══════════════════════════════════════════════════════════
  const [inscriptosTarget, setInscriptosTarget] =
    useState<InscriptosTarget | null>(null);

 const openNewsInscriptos = (item: NewsTask) =>
  setInscriptosTarget({
    coleccion: "Noticias",
    id: item.id,
    titulo: item.Titulo,
    abierta: item.Inscripcion === true,
    exp: parseXpReward(item.Exp),
  });

const openEventInscriptos = (item: EventoTask) =>
  setInscriptosTarget({
    coleccion: "Eventos",
    id: item.id,
    titulo: item.descripcion || "Evento",
    abierta: item.inscripcion === true,
    exp: parseXpReward(item.exp),
  });

  // ══════════════════════════════════════════════════════════
  // NOTICIAS
  // ══════════════════════════════════════════════════════════
  const [news, setNews] = useState<NewsTask[]>([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [newsModalOpen, setNewsModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsTask | null>(null);
  const [newsForm] = Form.useForm();
  const newsVideo = Form.useWatch("Video", newsForm);
  const [savingNews, setSavingNews] = useState(false);

  const loadNews = async () => {
    setLoadingNews(true);
    try {
      const allNews = await GetAllNews();
      setNews(allNews);
    } catch (error) {
      message.error("No se pudieron cargar las noticias.");
    } finally {
      setLoadingNews(false);
    }
  };

  const openNewsModal = (item?: NewsTask) => {
    setEditingNews(item || null);
    newsForm.setFieldsValue(
      item
        ? {
            Titulo: item.Titulo,
            Subtitle: item.Subtitle,
            Cuerpo: item.Cuerpo,
            Imagen: item.Imagen,
            Video: item.Video,
            Exp: item.Exp || undefined,
            ExpDetalle: item.ExpDetalle,
            Inscripcion: item.Inscripcion === true,
            FechaFin:
              item.FechaFin && dayjs(item.FechaFin).isValid()
                ? dayjs(item.FechaFin)
                : undefined,
          }
        : {
            Titulo: "",
            Subtitle: "",
            Cuerpo: "",
            Imagen: "",
            Video: "",
            Exp: undefined,
            ExpDetalle: "",
            Inscripcion: false,
            FechaFin: undefined,
          },
    );
    setNewsModalOpen(true);
  };

  const closeNewsModal = () => {
    setNewsModalOpen(false);
    setEditingNews(null);
    newsForm.resetFields();
  };

  const handleSaveNews = async () => {
    try {
      const values = await newsForm.validateFields();
      setSavingNews(true);
      const video = (values.Video ?? "").trim();
      const exp = parseXpReward(values.Exp);
      const expDetalle = exp ? (values.ExpDetalle ?? "").trim() : "";
      const inscripcion = values.Inscripcion === true;
      // El DatePicker devuelve un dayjs: se guarda como ISO, igual que los Eventos.
      const fechaFin = values.FechaFin ? values.FechaFin.toISOString() : "";
      if (editingNews) {
        await UpdateNewsEntry(editingNews.id, {
          ...values,
          Video: video,
          Exp: exp,
          ExpDetalle: expDetalle,
          Inscripcion: inscripcion,
          FechaFin: fechaFin,
        });
      } else {
        await CreateNewsEntry(
          values.Titulo,
          values.Subtitle,
          values.Cuerpo,
          values.Imagen,
          video,
          exp,
          expDetalle,
          inscripcion,
          fechaFin,
        );
      }
      message.success("Noticia guardada correctamente.");
      closeNewsModal();
      void loadNews();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error("No se pudo guardar la noticia.");
    } finally {
      setSavingNews(false);
    }
  };

  const handleDeleteNews = async (item: NewsTask) => {
    try {
      // Firestore no borra subcolecciones solo: primero los inscriptos.
      await DeleteAllInscriptos("Noticias", item.id);
      await DeleteNewsEntry(item.id);
      message.success("Noticia eliminada.");
      void loadNews();
    } catch (error) {
      message.error("No se pudo eliminar la noticia.");
    }
  };

  const newsColumns = [
    { title: "Título", dataIndex: "Titulo", key: "Titulo" },
    { title: "Subtítulo", dataIndex: "Subtitle", key: "Subtitle" },
    {
      title: "XP",
      dataIndex: "Exp",
      key: "Exp",
      width: 90,
      render: (value: unknown) =>
        parseXpReward(value) ? <Tag color="purple">+{parseXpReward(value)}</Tag> : "-",
    },
    {
      title: "Finaliza",
      dataIndex: "FechaFin",
      key: "FechaFin",
      width: 150,
      render: (fechaFin: string) => {
        if (!fechaFin || !dayjs(fechaFin).isValid()) return "-";
        const fecha = dayjs(fechaFin).format("DD/MM/YYYY HH:mm");
        return isNewsFinished(fechaFin) ? (
          <Tag color="red" title={fecha}>
            Finalizada
          </Tag>
        ) : (
          fecha
        );
      },
    },
    {
      title: "Inscripción",
      dataIndex: "Inscripcion",
      key: "Inscripcion",
      width: 110,
      render: renderInscripcionTag,
    },
    {
      title: "Acciones",
      key: "actions",
      width: 280,
      render: (_: unknown, record: NewsTask) => (
        // stopPropagation: tocar estos botones no abre la lista de inscriptos.
        <Space onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            icon={<TeamOutlined />}
            onClick={() => openNewsInscriptos(record)}
          >
            Inscriptos
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openNewsModal(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar esta noticia?"
            description="También se borra su lista de inscriptos."
            okText="Eliminar"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={() => handleDeleteNews(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ══════════════════════════════════════════════════════════
  // EVENTOS
  // ══════════════════════════════════════════════════════════
  const [events, setEvents] = useState<EventoTask[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventoTask | null>(null);
  const [eventForm] = Form.useForm();
  const [savingEvent, setSavingEvent] = useState(false);

  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const allEvents = await GetAllEvents();
      setEvents(allEvents);
    } catch (error) {
      message.error("No se pudieron cargar los eventos.");
    } finally {
      setLoadingEvents(false);
    }
  };

  const openEventModal = (item?: EventoTask) => {
    setEditingEvent(item || null);
    eventForm.setFieldsValue(
      item
        ? {
            descripcion: item.descripcion,
            imagen: item.imagen || "",
            exp: item.exp || undefined,
            expDetalle: item.expDetalle || "",
            inscripcion: item.inscripcion === true,
            fecha:
              typeof item.fecha === "string" && item.fecha
                ? dayjs(item.fecha)
                : undefined,
          }
        : {
            descripcion: "",
            imagen: "",
            exp: undefined,
            expDetalle: "",
            inscripcion: false,
            fecha: undefined,
          },
    );
    setEventModalOpen(true);
  };

  const closeEventModal = () => {
    setEventModalOpen(false);
    setEditingEvent(null);
    eventForm.resetFields();
  };

  const handleSaveEvent = async () => {
    try {
      const values = await eventForm.validateFields();
      setSavingEvent(true);
      const fechaISO = values.fecha ? values.fecha.toISOString() : "";
      const exp = parseXpReward(values.exp);
      const expDetalle = exp ? (values.expDetalle ?? "").trim() : "";
      const inscripcion = values.inscripcion === true;
      if (editingEvent) {
        await UpdateEvent(editingEvent.id, {
          descripcion: values.descripcion,
          imagen: values.imagen,
          fecha: fechaISO,
          exp,
          expDetalle,
          inscripcion,
        });
      } else {
        await CreateNewEvent(
          values.descripcion,
          fechaISO,
          values.imagen,
          exp,
          expDetalle,
          inscripcion,
        );
      }
      message.success("Evento guardado correctamente.");
      closeEventModal();
      void loadEvents();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error("No se pudo guardar el evento.");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (item: EventoTask) => {
    try {
      // Firestore no borra subcolecciones solo: primero los inscriptos.
      await DeleteAllInscriptos("Eventos", item.id);
      await DeleteEvent(item.id);
      message.success("Evento eliminado.");
      void loadEvents();
    } catch (error) {
      message.error("No se pudo eliminar el evento.");
    }
  };

  const eventColumns = [
    { title: "Descripción", dataIndex: "descripcion", key: "descripcion" },
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      render: (fecha: unknown) => {
        if (!fecha || typeof fecha !== "string") return "-";
        const parsed = dayjs(fecha);
        return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "-";
      },
    },
    {
      title: "Inscripción",
      dataIndex: "inscripcion",
      key: "inscripcion",
      width: 110,
      render: renderInscripcionTag,
    },
    {
      title: "Acciones",
      key: "actions",
      width: 280,
      render: (_: unknown, record: EventoTask) => (
        // stopPropagation: tocar estos botones no abre la lista de inscriptos.
        <Space onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            icon={<TeamOutlined />}
            onClick={() => openEventInscriptos(record)}
          >
            Inscriptos
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEventModal(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar este evento?"
            description="También se borra su lista de inscriptos."
            okText="Eliminar"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={() => handleDeleteEvent(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ══════════════════════════════════════════════════════════
  // CATÁLOGO DE RECOMPENSAS (compartido para todos los usuarios)
  // ══════════════════════════════════════════════════════════
  const [catalog, setCatalog] = useState<RewardCatalogEntry[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [editingCatalogEntry, setEditingCatalogEntry] =
    useState<RewardCatalogEntry | null>(null);
  const [catalogForm] = Form.useForm();
  const [savingCatalogEntry, setSavingCatalogEntry] = useState(false);

  const loadCatalog = async () => {
    setLoadingCatalog(true);
    try {
      const catalogEntries = await GetRewardsCatalog();
      setCatalog(
        [...catalogEntries].sort((a, b) => a.Level - b.Level),
      );
    } catch (error) {
      message.error("No se pudo cargar el catálogo de recompensas.");
    } finally {
      setLoadingCatalog(false);
    }
  };

  const openCatalogModal = (item?: RewardCatalogEntry) => {
    setEditingCatalogEntry(item || null);
    catalogForm.setFieldsValue(
      item
        ? {
            Name: item.Name,
            Description: item.Description,
            Category: item.Category,
            Icon: item.Icon,
            Rarity: item.Rarity,
            Tone: item.Tone,
            Level: item.Level,
            ExpRequired: item.ExpRequired,
            Image: item.Image,
            RedeemEnabled: item.RedeemEnabled,
            RedeemTitle: item.RedeemTitle,
            RedeemInstructions: item.RedeemInstructions,
            RedeemImage: item.RedeemImage,
          }
        : {
            Name: "",
            Description: "",
            Category: "special",
            Icon: "",
            Rarity: "common",
            Tone: "teal",
            Level: (catalog[catalog.length - 1]?.Level ?? 0) + 1,
            ExpRequired: 0,
            Image: "",
            RedeemEnabled: false,
            RedeemTitle: "",
            RedeemInstructions: "",
            RedeemImage: "",
          },
    );
    setCatalogModalOpen(true);
  };

  const closeCatalogModal = () => {
    setCatalogModalOpen(false);
    setEditingCatalogEntry(null);
    catalogForm.resetFields();
  };

  const handleSaveCatalogEntry = async () => {
    try {
      const values = await catalogForm.validateFields();
      setSavingCatalogEntry(true);

      const payload: RewardCatalogEntryInput = {
        ...values,
        RedeemEnabled: Boolean(values.RedeemEnabled),
        RedeemTitle: values.RedeemTitle ?? "",
        RedeemInstructions: values.RedeemInstructions ?? "",
        RedeemImage: values.RedeemImage ?? "",
      };

      if (editingCatalogEntry) {
        await UpdateRewardCatalogEntry(editingCatalogEntry.id, payload);
      } else {
        await CreateRewardCatalogEntry(payload);
      }
      invalidateRewardsCatalog();
      message.success("Recompensa guardada correctamente.");
      closeCatalogModal();
      void loadCatalog();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error("No se pudo guardar la recompensa.");
    } finally {
      setSavingCatalogEntry(false);
    }
  };

  const handleDeleteCatalogEntry = async (item: RewardCatalogEntry) => {
    try {
      await DeleteRewardCatalogEntry(item.id);
      invalidateRewardsCatalog();
      message.success("Recompensa eliminada.");
      void loadCatalog();
    } catch (error) {
      message.error("No se pudo eliminar la recompensa.");
    }
  };

  const catalogColumns = [
    {
      title: "Imagen",
      dataIndex: "Image",
      key: "Image",
      width: 70,
      render: (imageUrl: string) =>
        imageUrl ? (
          <Image
            src={imageUrl}
            width={40}
            height={40}
            style={{ objectFit: "contain", borderRadius: 8 }}
            fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjZWVlIi8+PC9zdmc+"
          />
        ) : (
          "-"
        ),
    },
    { title: "Nombre", dataIndex: "Name", key: "Name" },
    { title: "Nivel", dataIndex: "Level", key: "Level", width: 80 },
    { title: "Categoría", dataIndex: "Category", key: "Category", width: 110 },
    { title: "Rareza", dataIndex: "Rarity", key: "Rarity", width: 110 },
    {
      title: "Acciones",
      key: "actions",
      width: 170,
      render: (_: unknown, record: RewardCatalogEntry) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openCatalogModal(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar esta recompensa del catálogo?"
            description="Deja de mostrarse para todos los usuarios."
            okText="Eliminar"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={() => handleDeleteCatalogEntry(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    void loadUsers();
    void loadNews();
    void loadEvents();
    void loadCatalog();
    // Solo al montar: cada tab tiene su propio botón "Actualizar" para refrescar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1300, margin: "0 auto", width: "100%" }}>
        <Card
          className="app-glass"
          style={{
            borderRadius: 24,
            border: "1px solid var(--dashboard-panel-border)",
            boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
          }}
          bodyStyle={{ padding: 24 }}
        >
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Typography.Text
              strong
              style={{
                color: "var(--dashboard-header-blue)",
                textTransform: "uppercase",
                letterSpacing: 1.2,
              }}
            >
              Administración
            </Typography.Text>
            <Typography.Title
              level={2}
              style={{ margin: 0, color: "var(--dashboard-header-blue)" }}
            >
              Panel de administración
            </Typography.Title>
            <Typography.Text style={{ color: "var(--dashboard-text)" }}>
              Editá usuarios, noticias, eventos y el catálogo de recompensas
              directamente en Firestore. Crear o eliminar cuentas de acceso
              (login) se sigue haciendo desde Unity.
            </Typography.Text>
          </Space>
        </Card>

        <Card
          className="app-glass"
          style={{
            marginTop: 16,
            borderRadius: 24,
            border: "1px solid var(--dashboard-panel-border)",
            boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
          }}
          bodyStyle={{ padding: 20 }}
        >
          <Tabs
            defaultActiveKey="usuarios"
            items={[
              {
                key: "usuarios",
                label: "Usuarios",
                children: (
                  <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Space
                      wrap
                      style={{
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Input.Search
                        placeholder="Buscar por nombre o email"
                        allowClear
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        style={{ maxWidth: 320 }}
                      />
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => void loadUsers()}
                        loading={loadingUsers}
                      >
                        Actualizar
                      </Button>
                      <Button onClick={async () => {
  const n = await SyncAllPublicProfiles(users);
  message.success(`Perfiles públicos actualizados: ${n}`);
}}>
  Sincronizar perfiles del chat
</Button>
                    </Space>
                    <Table
                      rowKey="id"
                      columns={userColumns as any}
                      dataSource={filteredUsers}
                      loading={loadingUsers}
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: true }}
                    />
                  </Space>
                ),
              },
              {
                key: "importar-xp",
                label: "Importar XP",
                children: <XpImportTab onApplied={() => void loadUsers()} />,
              },
              {
                key: "recompensas",
                label: "Recompensas",
                children: (
                  <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Typography.Text style={{ color: "var(--dashboard-text)" }}>
                      Este catálogo es único para toda la app: lo que edites,
                      crees o elimines acá se refleja para todos los usuarios,
                      sin tener que entrar a cada uno.
                    </Typography.Text>
                    <Space
                      wrap
                      style={{
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openCatalogModal()}
                      >
                        Nueva recompensa
                      </Button>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => void loadCatalog()}
                        loading={loadingCatalog}
                      >
                        Actualizar
                      </Button>
                    </Space>
                    <Table
                      rowKey="id"
                      columns={catalogColumns as any}
                      dataSource={catalog}
                      loading={loadingCatalog}
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: true }}
                    />
                  </Space>
                ),
              },
              {
                key: "noticias",
                label: "Noticias",
                children: (
                  <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Space
                      wrap
                      style={{
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openNewsModal()}
                      >
                        Nueva noticia
                      </Button>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => void loadNews()}
                        loading={loadingNews}
                      >
                        Actualizar
                      </Button>
                    </Space>
                    <Typography.Text type="secondary">
                      Tocá una noticia para ver la lista de inscriptos.
                    </Typography.Text>
                    <Table
                      rowKey="id"
                      columns={newsColumns as any}
                      dataSource={news}
                      loading={loadingNews}
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: true }}
                      onRow={(record: NewsTask) => ({
                        onClick: () => openNewsInscriptos(record),
                        style: { cursor: "pointer" },
                      })}
                    />
                  </Space>
                ),
              },
              {
                key: "eventos",
                label: "Eventos",
                children: (
                  <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Space
                      wrap
                      style={{
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openEventModal()}
                      >
                        Nuevo evento
                      </Button>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => void loadEvents()}
                        loading={loadingEvents}
                      >
                        Actualizar
                      </Button>
                    </Space>
                    <Typography.Text type="secondary">
                      Tocá un evento para ver la lista de inscriptos.
                    </Typography.Text>
                    <Table
                      rowKey="id"
                      columns={eventColumns as any}
                      dataSource={events}
                      loading={loadingEvents}
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: true }}
                      onRow={(record: EventoTask) => ({
                        onClick: () => openEventInscriptos(record),
                        style: { cursor: "pointer" },
                      })}
                    />
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </div>

      {/* Drawer de edición de usuario */}
      <Drawer
        title={
          editingUser
            ? `Editar: ${editingUser.Nombre || editingUser.Email}`
            : "Editar usuario"
        }
        open={userDrawerOpen}
        onClose={closeUserDrawer}
        width={560}
        extra={
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={savingUser}
            onClick={() => void handleSaveUser()}
          >
            Guardar
          </Button>
        }
      >
        <Form form={userForm} layout="vertical">
          <Form.Item name="Nombre" label="Nombre">
            <Input />
          </Form.Item>
          <Form.Item name="Email" label="Email">
            <Input disabled />
          </Form.Item>
          <Form.Item name="Rol" label="Rol">
            <Select
              options={[
                { value: "regular", label: "regular" },
                { value: "admin", label: "admin" },
              ]}
            />
          </Form.Item>
          <Space style={{ width: "100%" }} size={12}>
            <Form.Item name="Nivel" label="Nivel" style={{ flex: 1 }}>
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="CurrentExp" label="Exp. actual" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="Puntos" label="Puntos" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Space>
          <Form.Item name="PortraitImg" label="URL de imagen de perfil">
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>

        <Divider>Skills</Divider>
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {skillsDraft.map((skill, index) => (
            <Space key={index} style={{ width: "100%" }}>
              <Input
                value={skill.Materia}
                onChange={(e) =>
                  updateSkillDraft(index, "Materia", e.target.value)
                }
                placeholder="Materia"
                style={{ width: 200 }}
              />
              <InputNumber
                value={skill.Progress}
                onChange={(value) =>
                  updateSkillDraft(index, "Progress", Number(value ?? 0))
                }
                min={0}
                placeholder="Progreso (exp)"
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeSkillDraft(index)}
              />
            </Space>
          ))}
          <Button icon={<PlusOutlined />} onClick={addSkillDraft}>
            Agregar skill
          </Button>
        </Space>

        <Divider>Logros</Divider>
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {logrosDraft.map((logro, index) => (
            <Space key={index} wrap style={{ width: "100%" }}>
              <Input
                value={logro.Name}
                onChange={(e) =>
                  updateLogroDraft(index, "Name", e.target.value)
                }
                placeholder="Nombre del logro"
                style={{ width: 180 }}
              />
              <Input
                value={logro.Description}
                onChange={(e) =>
                  updateLogroDraft(index, "Description", e.target.value)
                }
                placeholder="Descripción"
                style={{ width: 200 }}
              />
              <Select
                value={logro.Status}
                onChange={(value) => updateLogroDraft(index, "Status", value)}
                style={{ width: 140 }}
                options={[
                  { value: "Completado", label: "Completado" },
                  { value: "Pendiente", label: "Pendiente" },
                ]}
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeLogroDraft(index)}
              />
            </Space>
          ))}
          <Button icon={<PlusOutlined />} onClick={addLogroDraft}>
            Agregar logro
          </Button>
        </Space>

        <Divider>Notificaciones</Divider>
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {notificationsDraft.map((notif, index) => (
            <Space key={index} wrap style={{ width: "100%" }}>
              <Input
                value={notif.Title}
                onChange={(e) =>
                  updateNotificationDraft(index, "Title", e.target.value)
                }
                placeholder="Título"
                style={{ width: 180 }}
              />
              <Input
                value={notif.Description}
                onChange={(e) =>
                  updateNotificationDraft(
                    index,
                    "Description",
                    e.target.value,
                  )
                }
                placeholder="Descripción"
                style={{ width: 220 }}
              />
              <Switch
                checked={notif.Checked}
                onChange={(checked) =>
                  updateNotificationDraft(index, "Checked", checked)
                }
                checkedChildren="Leída"
                unCheckedChildren="Nueva"
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeNotificationDraft(index)}
              />
            </Space>
          ))}
          <Button icon={<PlusOutlined />} onClick={addNotificationDraft}>
            Agregar notificación
          </Button>
        </Space>
      </Drawer>

      {/* Modal de Noticias */}
      <Modal
        title={editingNews ? "Editar noticia" : "Nueva noticia"}
        open={newsModalOpen}
        onCancel={closeNewsModal}
        onOk={() => void handleSaveNews()}
        confirmLoading={savingNews}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={newsForm} layout="vertical">
          <Form.Item
            name="Titulo"
            label="Título"
            rules={[{ required: true, message: "Ingresá un título" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="Subtitle" label="Subtítulo">
            <Input />
          </Form.Item>
          <Form.Item
            name="Cuerpo"
            label="Cuerpo"
            rules={[{ required: true, message: "Ingresá el contenido" }]}
          >
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item name="Imagen" label="URL de imagen">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="Video"
            label="Video de YouTube (opcional)"
            extra="Pegá el link del video. Sirven links de youtube.com, youtu.be, Shorts y transmisiones."
            rules={[
              {
                validator: (_, value) =>
                  !value || !String(value).trim() || getYouTubeId(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Ese link no es de un video de YouTube.")),
              },
            ]}
          >
            <Input placeholder="https://www.youtube.com/watch?v=..." allowClear />
          </Form.Item>
          {getYouTubeId(newsVideo) ? (
            <YouTubeEmbed
              url={newsVideo}
              title="Vista previa"
              stopSignal={Number(newsModalOpen)}
            />
          ) : null}
          <Form.Item
            name="Exp"
            label="XP que se puede ganar (opcional)"
            extra="Dejalo vacío si esta noticia no suma XP."
            style={{ marginTop: 16 }}
          >
            <InputNumber min={0} step={10} precision={0} style={{ width: "100%" }} placeholder="Ej: 50" />
          </Form.Item>
          <Form.Item name="ExpDetalle" label="Cómo se gana (opcional)">
            <TextArea rows={2} placeholder="Ej: Completá la encuesta antes del viernes" />
          </Form.Item>
          <Form.Item
            name="FechaFin"
            label="Fecha de finalización (opcional)"
            extra="Desde ese día y hora la noticia deja de mostrarse a los usuarios. Dejalo vacío para que no venza."
          >
            <DatePicker
              showTime={{ format: "HH:mm" }}
              format="DD/MM/YYYY HH:mm"
              style={{ width: "100%" }}
              placeholder="Sin fecha de finalización"
            />
          </Form.Item>
          <Form.Item
            name="Inscripcion"
            label='Mostrar botón "Inscribirme"'
            valuePropName="checked"
            extra="Los usuarios pueden anotarse y ves la lista tocando la noticia en la tabla."
          >
            <Switch checkedChildren="Sí" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Eventos */}
      <Modal
        title={editingEvent ? "Editar evento" : "Nuevo evento"}
        open={eventModalOpen}
        onCancel={closeEventModal}
        onOk={() => void handleSaveEvent()}
        confirmLoading={savingEvent}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={eventForm} layout="vertical">
          <Form.Item
            name="descripcion"
            label="Descripción"
            rules={[{ required: true, message: "Ingresá una descripción" }]}
          >
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="fecha" label="Fecha">
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="imagen" label="URL de imagen">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="exp"
            label="XP que se puede ganar (opcional)"
            extra="Dejalo vacío si este evento no suma XP."
          >
            <InputNumber min={0} step={10} precision={0} style={{ width: "100%" }} placeholder="Ej: 100" />
          </Form.Item>
          <Form.Item name="expDetalle" label="Cómo se gana (opcional)">
            <TextArea rows={2} placeholder="Ej: Presentate en el stand y subí una foto" />
          </Form.Item>
          <Form.Item
            name="inscripcion"
            label='Mostrar botón "Inscribirme"'
            valuePropName="checked"
            extra="Los usuarios pueden anotarse y ves la lista tocando el evento en la tabla."
          >
            <Switch checkedChildren="Sí" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Recompensas (catálogo compartido) */}
      <Modal
        title={editingCatalogEntry ? "Editar recompensa" : "Nueva recompensa"}
        open={catalogModalOpen}
        onCancel={closeCatalogModal}
        onOk={() => void handleSaveCatalogEntry()}
        confirmLoading={savingCatalogEntry}
        okText="Guardar"
        cancelText="Cancelar"
        width={560}
      >
        <Form form={catalogForm} layout="vertical">
          <Form.Item
            name="Name"
            label="Nombre"
            rules={[{ required: true, message: "Ingresá un nombre" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="Description" label="Descripción">
            <TextArea rows={2} />
          </Form.Item>
          <Space style={{ width: "100%" }} size={12}>
            <Form.Item
              name="Level"
              label="Nivel"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Ingresá el nivel" }]}
            >
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="ExpRequired" label="Exp. requerida" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Space>
          <Space style={{ width: "100%" }} size={12}>
            <Form.Item name="Category" label="Categoría" style={{ flex: 1 }}>
              <Select options={REWARD_CATEGORY_OPTIONS} />
            </Form.Item>
            <Form.Item name="Rarity" label="Rareza" style={{ flex: 1 }}>
              <Select options={REWARD_RARITY_OPTIONS} />
            </Form.Item>
            <Form.Item name="Tone" label="Tono" style={{ flex: 1 }}>
              <Select options={REWARD_TONE_OPTIONS} />
            </Form.Item>
          </Space>
          <Form.Item name="Icon" label="Ícono / emoji (opcional)">
            <Input placeholder="🎁" />
          </Form.Item>
          <Form.Item name="Image" label="URL de imagen">
            <Input placeholder="https://..." />
          </Form.Item>

          <Divider style={{ margin: "8px 0" }}>Instrucciones de canje</Divider>

          <Form.Item
            name="RedeemEnabled"
            label="Mostrar botón «Cómo canjear» en la tarjeta"
            valuePropName="checked"
            extra="Dejalo apagado para premios que no necesitan explicación."
          >
            <Switch />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.RedeemEnabled !== cur.RedeemEnabled}
          >
            {({ getFieldValue }) =>
              getFieldValue("RedeemEnabled") ? (
                <>
                  <Form.Item name="RedeemTitle" label="Título del popup (opcional)">
                    <Input placeholder="Cómo canjear este premio" />
                  </Form.Item>
                  <Form.Item
                    name="RedeemImage"
                    label="Imagen del canje (opcional)"
                    extra="Puede ser un QR, un cupón o el logo del comercio. Si no cargás nada, el popup muestra solo el texto."
                  >
                    <Input placeholder="https://..." />
                  </Form.Item>
                  <Form.Item
                    name="RedeemInstructions"
                    label="Pasos para canjear"
                    extra="Un paso por línea. Los saltos de línea se respetan en el popup."
                  >
                    <TextArea
                      rows={5}
                      placeholder={
                        "- Escaneá el QR\n- Mostrá tu usuario del Desafío\n- Válido hasta el 30/09"
                      }
                    />
                  </Form.Item>
                </>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>

      {/* Lista de inscriptos de un evento o noticia (11/10/2026) */}
      <InscriptosModal
        target={inscriptosTarget}
        onClose={() => setInscriptosTarget(null)}
      />
    </DashboardLayout>
  );
};

export default Admin;
